import React, { useState, useEffect } from "react";
import { 
  buscarDispositivosDisponiveis, 
  conectarDispositivo, 
  desconectarDispositivo, 
  verificarStatusDispositivo,
  enviarProgramacao
} from "../../services/deviceService";
import { getMeusMedicamentos } from "../../services/authService";
import "./Dispositivo.scss";

const initialGrid = Array(3).fill(null).map(() => Array(3).fill(null));

const Dispositivo = () => {
  const [metodoConexao, setMetodoConexao] = useState("bluetooth");
  const [dispositivosDisponiveis, setDispositivosDisponiveis] = useState([]);
  const [dispositivoSelecionado, setDispositivoSelecionado] = useState(null);
  const [dispositivoConectado, setDispositivoConectado] = useState(null);
  const [statusDispositivo, setStatusDispositivo] = useState(null);
  const [medicamentos, setMedicamentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [mensagem, setMensagem] = useState(null);
  const [buscando, setBuscando] = useState(false);
  const [enviandoProgramacao, setEnviandoProgramacao] = useState(false);
  const [resultadosEnvio, setResultadosEnvio] = useState(null);
  const [mostrarResultados, setMostrarResultados] = useState(false);
  const [grid, setGrid] = useState(initialGrid);
  const [medicamentoSelecionado, setMedicamentoSelecionado] = useState(null);
  const [mostrarSelecaoCompartimentos, setMostrarSelecaoCompartimentos] = useState(false);

  useEffect(() => {
    const carregarMedicamentos = async () => {
      try {
        const data = await getMeusMedicamentos();
        setMedicamentos(data);
        atualizarGrid(data);
      } catch (error) {
        console.error("Erro ao carregar medicamentos:", error);
      }
    };

    carregarMedicamentos();
  }, []);

  // Atualizar o grid com os medicamentos da Home
  const atualizarGrid = (medicamentosList) => {
    const newGrid = initialGrid.map((row, rowIndex) =>
      row.map((_, colIndex) =>
        medicamentosList.find(m => m.linha === rowIndex && m.coluna === colIndex) || null
      )
    );
    setGrid(newGrid);
  };

  useEffect(() => {
    // Verificar status do dispositivo periodicamente se estiver conectado
    let intervalo;
    if (dispositivoConectado) {
      intervalo = setInterval(async () => {
        try {
          const status = await verificarStatusDispositivo(dispositivoConectado);
          setStatusDispositivo(status);

          // Se o dispositivo estiver desconectado, atualizar estado
          if (status.status === "desconectado") {
            setDispositivoConectado(null);
            clearInterval(intervalo);
          }
        } catch (error) {
          console.error("Erro ao verificar status:", error);
        }
      }, 5000); // Verificar a cada 5 segundos
    }

    return () => {
      if (intervalo) clearInterval(intervalo);
    };
  }, [dispositivoConectado]);

  const handleMetodoConexaoChange = (e) => {
    setMetodoConexao(e.target.value);
    setDispositivosDisponiveis([]);
    setDispositivoSelecionado(null);
    setErro(null);
  };

  const buscarDispositivos = async () => {
    try {
      setErro(null);
      setBuscando(true);
      const dispositivos = await buscarDispositivosDisponiveis(metodoConexao);
      setDispositivosDisponiveis(dispositivos);
      setBuscando(false);
    } catch (error) {
      setErro("Erro ao buscar dispositivos. Verifique suas permissões e conexões.");
      setBuscando(false);
    }
  };

  const conectar = async () => {
    if (!dispositivoSelecionado) {
      setErro("Selecione um dispositivo para conectar.");
      return;
    }

    try {
      setLoading(true);
      setErro(null);
      setMensagem(null);
      
      const resultado = await conectarDispositivo(dispositivoSelecionado, metodoConexao);
      
      if (resultado.sucesso) {
        setDispositivoConectado(resultado.dispositivo);
        setMensagem(`Conectado com sucesso a ${resultado.dispositivo.nome}`);
        
        // Verificar status inicial
        const status = await verificarStatusDispositivo(resultado.dispositivo);
        setStatusDispositivo(status);
      }
      
      setLoading(false);
    } catch (error) {
      setErro(typeof error === 'string' ? error : error.message || "Erro ao conectar ao dispositivo");
      setLoading(false);
    }
  };

  const desconectar = async () => {
    try {
      setLoading(true);
      await desconectarDispositivo(dispositivoConectado);
      setDispositivoConectado(null);
      setStatusDispositivo(null);
      setMensagem("Dispositivo desconectado com sucesso");
      setLoading(false);
    } catch (error) {
      setErro("Erro ao desconectar dispositivo");
      setLoading(false);
    }
  };

  const enviarDados = async () => {
    if (!dispositivoConectado) {
      setErro("Conecte-se a um dispositivo primeiro");
      return;
    }

    // Verificar se há medicamentos registrados
    const medicamentosRegistrados = medicamentos.filter(m => m.linha !== undefined && m.coluna !== undefined);
    if (medicamentosRegistrados.length === 0) {
      setErro("Não há medicamentos registrados na caixa");
      return;
    }

    // Abrir tela de seleção de compartimentos
    setMostrarSelecaoCompartimentos(true);
  };

  const handleSlotClick = (row, col) => {
    const medicamento = grid[row][col];
    
    if (!medicamento) {
      // Não faz nada se o slot estiver vazio
      return;
    }

    // Selecionar ou desselecionar o medicamento
    if (medicamentoSelecionado && medicamentoSelecionado.id === medicamento.id) {
      setMedicamentoSelecionado(null);
    } else {
      setMedicamentoSelecionado(medicamento);
      
      // Calcular o número do compartimento (1-9)
      const compartimento = row * 3 + col + 1;
      medicamento.compartimento = compartimento;
    }
  };

  const enviarMedicamentoSelecionado = async () => {
    if (!medicamentoSelecionado) {
      setErro("Selecione um medicamento para enviar");
      return;
    }

    try {
      setEnviandoProgramacao(true);
      setErro(null);
      setMensagem(null);
      setMostrarSelecaoCompartimentos(false);
      
      // Enviar apenas o medicamento selecionado
      const resultado = await enviarProgramacao(dispositivoConectado, [medicamentoSelecionado]);
      
      if (resultado.sucesso) {
        setMensagem(resultado.mensagem);
        if (resultado.detalhes && resultado.detalhes.resultados) {
          setResultadosEnvio(resultado.detalhes.resultados);
          setMostrarResultados(true);
        }
      }
      
      setMedicamentoSelecionado(null);
      setEnviandoProgramacao(false);
    } catch (error) {
      setErro("Erro ao enviar programação para o dispositivo: " + error.message);
      setEnviandoProgramacao(false);
    }
  };

  const fecharResultados = () => {
    setMostrarResultados(false);
    setResultadosEnvio(null);
  };

  const renderStatusIndicador = () => {
    if (!statusDispositivo) return null;

    let statusClass = "status-indicador ";
    let statusText = "";

    switch (statusDispositivo.status) {
      case "conectado":
        statusClass += "conectado";
        statusText = "Conectado";
        break;
      case "desconectado":
        statusClass += "desconectado";
        statusText = "Desconectado";
        break;
      case "ocupado":
        statusClass += "ocupado";
        statusText = "Ocupado";
        break;
      case "erro":
        statusClass += "erro";
        statusText = "Erro";
        break;
      default:
        statusClass += "desconhecido";
        statusText = "Estado Desconhecido";
    }

    return (
      <div className="status-container">
        <div className={statusClass}>
          <span className="status-dot"></span>
          <span className="status-texto">{statusText}</span>
        </div>
        <div className="bateria-indicador">
          <i className="fas fa-battery-half"></i>
          <span>{statusDispositivo.bateria}%</span>
        </div>
      </div>
    );
  };

  return (
    <div className="dispositivo-wrapper fade-in">
      <h1>Conexão com Dispositivo</h1>
      
      {erro && <div className="mensagem erro">{erro}</div>}
      {mensagem && <div className="mensagem sucesso">{mensagem}</div>}
      
      <div className="dispositivo-container">
        <div className="dispositivo-header">
          <div className="dispositivo-icon">
            <i className="fas fa-microchip"></i>
            <h2>Conecte sua Caixa de Medicamentos</h2>
          </div>
          
          {dispositivoConectado && renderStatusIndicador()}
        </div>
        
        {!dispositivoConectado ? (
          <div className="conexao-section">
            <div className="form-group">
              <label htmlFor="metodoConexao">Método de Conexão</label>
              <select 
                id="metodoConexao" 
                value={metodoConexao} 
                onChange={handleMetodoConexaoChange}
                disabled={loading}
              >
                <option value="serial">Serial (USB)</option>
                <option value="bluetooth">Bluetooth</option>
                <option value="wifi">Wi-Fi</option>
              </select>
            </div>
            
            <div className="metodo-info">
              {metodoConexao === "serial" && (
                <div className="info-card">
                  <i className="fas fa-usb"></i>
                  <p>Conecte seu dispositivo via cabo USB e certifique-se que os drivers estão instalados.</p>
                </div>
              )}
              
              {metodoConexao === "bluetooth" && (
                <div className="info-card">
                  <i className="fab fa-bluetooth-b"></i>
                  <p>Certifique-se que o Bluetooth do seu computador está ligado e o dispositivo está em modo de pareamento.</p>
                </div>
              )}
              
              {metodoConexao === "wifi" && (
                <div className="info-card">
                  <i className="fas fa-wifi"></i>
                  <p>Seu dispositivo deve estar conectado à mesma rede Wi-Fi que seu computador.</p>
                </div>
              )}
            </div>
            
            <div className="busca-dispositivos">
              <button 
                className="scan-btn" 
                onClick={buscarDispositivos} 
                disabled={buscando || loading}
              >
                <i className="fas fa-search"></i>
                {buscando ? "Buscando..." : "Buscar Dispositivos"}
              </button>
            </div>
            
            {dispositivosDisponiveis.length > 0 && (
              <div className="dispositivos-list">
                <h3>Dispositivos Disponíveis</h3>
                <div className="list-container">
                  {dispositivosDisponiveis.map((dispositivo) => (
                    <div 
                      key={dispositivo.id} 
                      className={`dispositivo-item ${dispositivoSelecionado?.id === dispositivo.id ? 'selecionado' : ''}`}
                      onClick={() => setDispositivoSelecionado(dispositivo)}
                    >
                      <div className="dispositivo-info">
                        <span className="dispositivo-nome">{dispositivo.nome}</span>
                        <span className="dispositivo-tipo">{dispositivo.tipo}</span>
                      </div>
                      <div className="conexao-info">
                        <span className="ultima-conexao">
                          <i className="fas fa-clock"></i>
                          {new Date(dispositivo.ultimaConexao).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
                
                <button 
                  className="conectar-btn"
                  disabled={!dispositivoSelecionado || loading}
                  onClick={conectar}
                >
                  {loading ? (
                    <span>Conectando...</span>
                  ) : (
                    <>
                      <i className="fas fa-plug"></i>
                      Conectar
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div className="dispositivo-conectado-section">
            <div className="dispositivo-info-card">
              <div className="dispositivo-detalhe">
                <h3>{dispositivoConectado.nome}</h3>
                <span className="tipo">{dispositivoConectado.tipo}</span>
                <span className="metodo-conexao">
                  {metodoConexao === "serial" && <i className="fas fa-usb"></i>}
                  {metodoConexao === "bluetooth" && <i className="fab fa-bluetooth-b"></i>}
                  {metodoConexao === "wifi" && <i className="fas fa-wifi"></i>}
                  Conectado via {
                    metodoConexao === "serial" ? "USB" :
                    metodoConexao === "bluetooth" ? "Bluetooth" : "Wi-Fi"
                  }
                </span>
              </div>
              
              <div className="dispositivo-acoes">
                <button 
                  className="enviar-btn"
                  onClick={enviarDados}
                  disabled={enviandoProgramacao || loading || medicamentos.length === 0}
                >
                  {enviandoProgramacao ? (
                    <span>Enviando...</span>
                  ) : (
                    <>
                      <i className="fas fa-upload"></i>
                      Enviar Programação
                    </>
                  )}
                </button>
                
                <button 
                  className="desconectar-btn"
                  onClick={desconectar}
                  disabled={loading}
                >
                  <i className="fas fa-unlink"></i>
                  Desconectar
                </button>
              </div>
            </div>
            
            <div className="medicamentos-contagem">
              <div className="contagem-card">
                <i className="fas fa-pills"></i>
                <div className="contagem-info">
                  <span className="contagem-label">Medicamentos na caixa</span>
                  <span className="contagem-valor">{medicamentos.filter(m => m.linha !== undefined && m.coluna !== undefined).length}</span>
                </div>
              </div>
            </div>
            
            <div className="conexao-instrucoes">
              <h4>Instruções</h4>
              <ol>
                <li>Certifique-se que sua caixa de medicamentos está ligada e funcionando corretamente.</li>
                <li>Clique em "Enviar Programação" para selecionar qual medicamento deseja programar.</li>
                <li>Selecione o medicamento desejado no grid e clique em "Enviar".</li>
                <li>A caixa abrirá o compartimento correto no horário programado.</li>
              </ol>
            </div>
          </div>
        )}
      </div>
      
      {/* Modal de seleção de compartimentos com grid */}
      {mostrarSelecaoCompartimentos && (
        <div className="modal-overlay">
          <div className="modal-content grid-modal">
            <h2>Selecione um medicamento para enviar</h2>
            <p>
              {medicamentoSelecionado 
                ? `Medicamento selecionado: "${medicamentoSelecionado.nome}" (Compartimento ${medicamentoSelecionado.compartimento})` 
                : "Clique em um medicamento no grid para selecioná-lo"}
            </p>
            
            <div className="grid-container-single">
              <div className="compartimentos-grid">
                <h3>Caixa de Medicamentos</h3>
                <div className="grid">
                  {grid.map((row, rowIndex) => (
                    <div key={rowIndex} className="row">
                      {row.map((slot, colIndex) => {
                        const compartimento = rowIndex * 3 + colIndex + 1;
                        const isSelected = medicamentoSelecionado && 
                                          medicamentoSelecionado.linha === rowIndex && 
                                          medicamentoSelecionado.coluna === colIndex;
                        
                        return (
                          <div
                            key={colIndex}
                            className={`slot ${slot ? "filled" : "empty"} ${isSelected ? "selected" : ""}`}
                            onClick={() => handleSlotClick(rowIndex, colIndex)}
                          >
                            {slot ? (
                              <div className="medicamento-content">
                                <div className="medicamento-info">
                                  <span className="nome">{slot.nome}</span>
                                </div>
                                <div className="compartimento-numero-filled">{compartimento}</div>
                                <div className="medicamento-details">
                                  <span>{slot.dosagem}</span>
                                  <span className="frequencia">{slot.frequencia}</span>
                                </div>
                              </div>
                            ) : (
                              <div className="empty-slot-content">
                                <div className="compartimento-numero">{compartimento}</div>
                                <div className="empty-text">
                                  <span>Vazio</span>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="modal-actions">
              <button 
                className="btn-cancelar"
                onClick={() => {
                  setMostrarSelecaoCompartimentos(false);
                  setMedicamentoSelecionado(null);
                }}
                disabled={enviandoProgramacao}
              >
                Cancelar
              </button>
              <button 
                className="btn-confirmar"
                onClick={enviarMedicamentoSelecionado}
                disabled={enviandoProgramacao || !medicamentoSelecionado}
              >
                {enviandoProgramacao ? "Enviando..." : "Enviar Medicamento"}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal de resultados do envio */}
      {mostrarResultados && resultadosEnvio && (
        <div className="modal-overlay">
          <div className="modal-content resultados-modal">
            <h2>Resultados do Envio</h2>
            
            <div className="resultados-lista">
              {resultadosEnvio.map((resultado, index) => (
                <div 
                  key={index} 
                  className={`resultado-item ${resultado.sucesso ? 'sucesso' : 'erro'}`}
                >
                  <div className="resultado-header">
                    <span className="med-nome">{resultado.medicamento}</span>
                    <span className="compartimento">Compartimento {resultado.compartimento}</span>
                  </div>
                  <div className="resultado-status">
                    {resultado.sucesso ? (
                      <span className="status-sucesso">✓ Programado com sucesso</span>
                    ) : (
                      <span className="status-erro">✗ Falha: {resultado.mensagem}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            
            <div className="modal-actions">
              <button 
                className="btn-confirmar"
                onClick={fecharResultados}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dispositivo; 